// Sinxronlaşma göstəricisi — Header-də kiçik çip + ilk sinxron üçün progress bar.
//
// İKİ AYRI HAL, İKİ AYRI GÖRÜNÜŞ:
//
//   1. İLK SİNXRON (cihaz təzə qeydiyyatdan keçib) — ekranın yuxarısında incə
//      progress bar. Bu, menyu/masa/personal-ın yüklənməsidir; işçi gözləməli
//      olduğunu bilməlidir, yoxsa boş ekranı nasazlıq sayır.
//
//   2. GÜNDƏLİK İŞ — yalnız kiçik çip. Sinxron arxa planda daim gedir; hər
//      dəfə iri bar göstərmək diqqəti yayındırardı.
//
// Faiz stream sayı ilə ölçülür (4 stream), event sayı ilə yox — bulud ümumi
// event sayını bildirmir. Uydurma faizdənsə dürüst ölçü seçilib; yanında
// tətbiq olunmuş event sayı da göstərilir ki, işin getdiyi görünsün.

import React from 'react';
import { AlertTriangle, Check, CloudOff, RefreshCw, UploadCloud } from 'lucide-react';

import { useI18n } from '../i18n/I18nContext';
import { useSyncStatus } from '../hook/useSyncStatus';

/** Ekranın ən üstündə incə tərəqqi zolağı — yalnız ilk sinxron zamanı. */
export const SyncProgressBar: React.FC = () => {
  const { enabled, initial, progress, applied, linked, error, revoked } = useSyncStatus();
  const { t } = useI18n();

  // Cihaz ləğvi hər şeydən üstündür və tərəqqi zolağından ASILI DEYİL:
  // sinxron heç vaxt başlamayacaq, ona görə "initial" şərtini gözləmək olmaz.
  // İşçinin görəcəyi iş də başqadır — internet gözləmək kömək etmir.
  if (enabled && revoked) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[200] flex justify-center px-4">
        <div className="mt-2 flex max-w-2xl items-start gap-2.5 rounded-2xl bg-red-700/95 px-4 py-2.5 text-xs text-white shadow-lg backdrop-blur">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0">
            <div className="font-bold">{t('sync.revoked')}</div>
            <div className="mt-0.5 text-[11px] text-red-100">{t('sync.revokedHint')}</div>
          </div>
        </div>
      </div>
    );
  }

  // Cihaz qeydiyyatdan keçməyibsə sinxron ümumiyyətlə başlamır — bar göstərmək
  // yanlış təəssürat yaradardı ("gözlə, yüklənir" — halbuki heç nə yüklənmir).
  if (!enabled || !linked || !initial) return null;

  const percent = Math.round(progress * 100);

  // Xəta varsa tərəqqi zolağı ALDADICIDIR: 0%-də dayanıb, sanki hələ
  // yüklənir. Əslində hər cəhd uğursuzdur. Səbəbi göstərmək lazımdır.
  if (error) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[200] flex justify-center px-4">
        <div className="mt-2 flex max-w-2xl items-start gap-2.5 rounded-2xl bg-red-600/95 px-4 py-2.5 text-xs text-white shadow-lg backdrop-blur">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0">
            <div className="font-bold">{t('sync.failed')}</div>
            <div className="mt-0.5 break-words font-mono text-[11px] text-red-100">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[200] pointer-events-none">
      <div className="h-1 w-full bg-black/10 dark:bg-white/10">
        <div
          className="h-full bg-[var(--primary)] transition-[width] duration-500 ease-out"
          style={{ width: `${Math.max(4, percent)}%` }}
        />
      </div>
      <div className="flex justify-center">
        <div className="mt-2 flex items-center gap-2 rounded-full bg-gray-900/90 dark:bg-[#2a2b31]/90 px-3 py-1.5 text-xs font-semibold text-white shadow-lg backdrop-blur">
          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          <span>{t('sync.initial', { percent })}</span>
          {applied > 0 && (
            <span className="text-gray-400 font-normal">
              {t('sync.applied', { n: applied })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/** Header-dəki kiçik çip. */
const SyncIndicator: React.FC = () => {
  const { enabled, syncing, linked, pending, applied, error, revoked } = useSyncStatus();
  const { t } = useI18n();

  if (!enabled) return null;

  if (revoked) {
    return (
      <Chip
        title={t('sync.revoked')}
        className="text-red-600 dark:text-red-400 bg-red-500/10"
        icon={<AlertTriangle className="w-4 h-4" />}
      />
    );
  }

  // Qeydiyyatdan keçməyib: bu, sükutla boş qalmaqdan qat-qat yaxşıdır.
  if (!linked) {
    return (
      <Chip
        title={t('sync.notLinked')}
        className="text-amber-600 dark:text-amber-400 bg-amber-500/10"
        icon={<CloudOff className="w-4 h-4" />}
      />
    );
  }

  // Xəta çipi hər şeydən üstündür: yaşıl "hazırdır" göstərmək yalan olardı.
  if (error) {
    return (
      <Chip
        title={t('sync.failedDetail', { message: error })}
        className="text-red-600 dark:text-red-400 bg-red-500/10"
        icon={<AlertTriangle className="w-4 h-4" />}
      />
    );
  }

  if (syncing) {
    return (
      <Chip
        title={applied > 0 ? t('sync.appliedTitle', { n: applied }) : t('sync.syncing')}
        className="text-orange-600 dark:text-orange-400 bg-orange-500/10"
        icon={<RefreshCw className="w-4 h-4 animate-spin" />}
      />
    );
  }

  // Oflayn işləyəndə outbox yığılır — işçi bunu bilməlidir ki, kompüteri
  // sıradan çıxarmasın: göndərilməmiş hadisələr yalnız bu maşındadır.
  if (pending > 0) {
    return (
      <Chip
        title={t('sync.pending', { n: pending })}
        className="text-sky-600 dark:text-sky-400 bg-sky-500/10"
        icon={<UploadCloud className="w-4 h-4" />}
        badge={pending}
      />
    );
  }

  return (
    <Chip
      title={t('sync.upToDate')}
      className="text-emerald-600 dark:text-emerald-400 bg-emerald-500/10"
      icon={<Check className="w-4 h-4" />}
    />
  );
};

const Chip: React.FC<{
  title: string;
  className: string;
  icon: React.ReactNode;
  badge?: number;
}> = ({ title, className, icon, badge }) => (
  <div
    title={title}
    aria-label={title}
    className={`relative flex items-center justify-center w-10 h-10 rounded-full transition ${className}`}
  >
    {icon}
    {badge !== undefined && badge > 0 && (
      <span className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-sky-500 text-white text-[10px] font-bold flex items-center justify-center">
        {badge > 99 ? '99+' : badge}
      </span>
    )}
  </div>
);

export default SyncIndicator;
